import { nowIsoAndUnix } from "../date-time/iso-and-unix";
import type {
    Percentage,
    PercentageKey} from "../percentage";
import {
    percentageZodSchema,
    roundPercentage,
    toPercentageFromPercentageKey,
} from "../percentage";
import type {
    AmountsAndVatRate,
    AmountsByVatRate,
    Price,
    PriceTerm} from "./price";
import {
    sortAmountsByVatRate,
    zeroTerm,
} from "./price";
import { priceIdFactory } from "./price-id";

const sumPrices = (prices: Price[], _log = false): Price => {
    const _prices = prices.filter(
        p => !(p.amountIncludingVat === 0 && p.vat === 0),
    );

    const sum = _prices.reduce((agg, price, _index) => {
        // Make sure they are all the same currency
        if (agg.currencyCode !== price.currencyCode)
            throw new Error(
                "Summarization of prices of different currencies not yet supported",
            );

        // Sum up the raw amounts
        agg.amountExcludingVat += price.amountExcludingVat;
        agg.amountIncludingVat += price.amountIncludingVat;

        // Calculate the full vat amount
        agg.vatAmount = agg.amountIncludingVat - agg.amountExcludingVat;

        // Vat amounts sorted by vat percentage
        const vatAmountsSorted: AmountsByVatRate = {
            ...Object.entries(sortAmountsByVatRate(price)).reduce(
                (acc, [key, data]) => {
                    const percentageKey: PercentageKey = key;
                    const amountsForVatRate: AmountsAndVatRate = {
                        amountIncludingVat:
                            (acc[percentageKey]?.amountIncludingVat || 0) +
                            data.amountIncludingVat,
                        amountExcludingVat:
                            (acc[percentageKey]?.amountExcludingVat || 0) +
                            data.amountExcludingVat,
                        vatAmount:
                            (acc[percentageKey]?.vatAmount || 0) +
                            data.vatAmount,
                        vatRate: toPercentageFromPercentageKey(percentageKey),
                    };
                    if (amountsForVatRate.vatAmount !== 0)
                        acc[percentageKey] = amountsForVatRate;
                    if (amountsForVatRate.vatAmount === 0)
                        delete acc[percentageKey];
                    return acc;
                },
                { ...(agg.vatAmounts || {}) } as AmountsByVatRate,
            ),
        };

        agg.vatAmounts = vatAmountsSorted;

        // log && console.log("SUM " + index, agg, price);

        return agg;
    }, zeroTerm());

    // if (
    //     isNaN(sum.vatAmount) ||
    //     isNaN(sum.amountExcludingVat) ||
    //     sum.amountExcludingVat === 0
    // ) {
    //     console.log(
    //         "NAN",
    //         sum.vatAmount,
    //         sum.amountExcludingVat,
    //         Math.abs(sum.vatAmount / sum.amountExcludingVat),
    //         sum.amountExcludingVat === 0
    //     );
    // }
    const vatRateCalc = (sum: PriceTerm): Percentage => {
        if (sum.amountExcludingVat === 0) return 0 as Percentage;
        if (sum.vatAmounts && Object.keys(sum.vatAmounts).length === 1)
            return toPercentageFromPercentageKey(
                Object.keys(sum.vatAmounts)[0],
            );

        //console.log("TOO BIG AND BUGGY", sum);
        return percentageZodSchema.parse(
            sum.vatAmount / sum.amountExcludingVat,
        );
        // try {
        // } catch (error) {
        // console.error(
        //     "VAT RATE CALC ERROR",
        //     sum,
        //     sum.vatAmount,
        //     sum.amountExcludingVat,
        // );
        // throw error;
        // }
    };
    const vatRate = vatRateCalc(sum);

    const result: Price = {
        _type: "PRICE",
        _createdAt: nowIsoAndUnix(),
        _id: priceIdFactory(),
        amountExcludingVat: sum.amountExcludingVat,
        amountIncludingVat: sum.amountIncludingVat,
        currencyCode: sum.currencyCode,
        vatAmounts: sum.vatAmounts,
        vat: vatRate,
        vatRounded: roundPercentage(vatRate),
        vatAmount: sum.vatAmount,
        tags: [],
    };

    // log && console.log("SUM RESULT", result);

    return result;
};

export { sumPrices };
